import { PropsWithChildren, useCallback, useEffect, useState } from 'react'
import { useSnackbar } from 'notistack'
import { useLocation, useNavigate } from 'react-router-dom'
import { LIMIT_TO_POLL_LEADS, TIME_TO_AWAIT_POLL_MS } from 'common/constants/env'
import i18n from 'common/i18n'
import { sleepAsync } from 'common/utils/time'
import PageLoading from 'containers/PageLoading'
import { useApiErrorHandler } from 'hooks'
import leadService from 'services/lead/http'
import migrationService from 'services/migration/http'

const MigrationGuard = ({ children }: PropsWithChildren<unknown>) => {
  const [loadingPage, setLoad] = useState(true)
  const { handleErrorSnackbar } = useApiErrorHandler()
  const location = useLocation()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()

  const pollMigrationStatus = useCallback(
    async (migrations: number, callCount = 0) => {
      try {
        if (migrations > 0) {
          const leadResult = await leadService.get()
          const finishedLoadMigratedLeads = leadResult.data.length >= migrations

          if (finishedLoadMigratedLeads) {
            setLoad(false)
          } else if (callCount < LIMIT_TO_POLL_LEADS) {
            await sleepAsync(TIME_TO_AWAIT_POLL_MS)
            await pollMigrationStatus(migrations, callCount + 1)
          } else {
            enqueueSnackbar(i18n.t<string>('onboarding.migration.leadErrors'), {
              variant: 'error',
            })
          }
        }
      } catch (error) {
        handleErrorSnackbar(error, {
          fallbackMessage: i18n.t<string>('onboarding.migration.leadErrors'),
        })
      } finally {
        setLoad(false)
      }
    },
    [handleErrorSnackbar, enqueueSnackbar]
  )

  const verifyIdMagaluMigration = useCallback(async () => {
    setLoad(true)

    const searchParams = new URLSearchParams(window.location.search)
    const isMigration = searchParams.get('migration')
    let migrations = 0

    try {
      if (isMigration === 'true') {
        const migrationCompleteResult = await migrationService.completeMigration()
        migrations = migrationCompleteResult.data.migrations

        searchParams.delete('migration')
        navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true })

        await pollMigrationStatus(migrations)
      }
    } catch (e) {
      handleErrorSnackbar(e, {
        fallbackMessage: i18n.t<string>('onboarding.migration.completeMigrationError'),
      })
    } finally {
      setLoad(false)
    }
  }, [handleErrorSnackbar, pollMigrationStatus, navigate, location.pathname])

  useEffect(() => {
    const fetchData = async () => {
      await verifyIdMagaluMigration()
    }
    fetchData()
  }, [verifyIdMagaluMigration])

  if (loadingPage) {
    return (
      <>
        {' '}
        <PageLoading />{' '}
      </>
    )
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>
}

export default MigrationGuard
